import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
// @mui
import { List, Collapse, Link, Box, IconButton, Tooltip } from '@mui/material';
import { BookmarkOutlined, BookmarkBorderOutlined } from '@mui/icons-material';
import NavItem from './NavItem';
import { getActive, isExternalLink } from '..';
import useNavPins from '../../../hooks/useNavPins';
import useAuth from '../../../hooks/useAuth';
import { isItemVisible } from '../../../guards/checkNavAccess';

// ----------------------------------------------------------------------

NavList.propTypes = {
  data: PropTypes.object,
  depth: PropTypes.number,
  hasChildren: PropTypes.bool,
  isCollapse: PropTypes.bool,
  editMode: PropTypes.bool,
};

export default function NavList({ data, depth, hasChildren, isCollapse = false, editMode = false }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isPinned, togglePin } = useNavPins();

  const active = getActive(data.path, pathname);
  const [open, setOpen] = useState(active);

  // A locked item is a paid module shown to a free-wedge org. It is drawn so
  // the operator can see what the paid product is, and it goes nowhere: there
  // is no page behind it they are entitled to.
  const isLocked = Boolean(data.lockReason);

  const handleClickItem = () => {
    if (isLocked) return;
    if (!hasChildren) {
      navigate(data.path);
    }
    setOpen(!open);
  };

  const pinned = isPinned(data.path);
  // Only show pin button for root-level items (depth=1) when in edit mode
  const showPinBtn = depth === 1 && !isCollapse && editMode;

  const navItemProps = {
    item: data,
    depth,
    open,
    active,
    isCollapse,
    onClick: handleClickItem,
    // For leaf items without children: use RouterLink so right-click "Open in new tab" works
    ...(!isLocked &&
      !hasChildren &&
      !isExternalLink(data.path) && { component: RouterLink, to: data.path }),
  };

  return (
    <>
      {isExternalLink(data.path) ? (
        <Link href={data.path} target="_blank" rel="noopener" underline="none">
          <NavItem item={data} depth={depth} open={open} active={active} isCollapse={isCollapse} />
        </Link>
      ) : (
        <Box sx={{ position: 'relative' }}>
          <NavItem
            {...navItemProps}
            // Nudge right padding slightly when pin button is visible so text doesn't underlap it
            sx={showPinBtn ? { pr: '28px' } : undefined}
          />

          {showPinBtn && (
            <Tooltip title={pinned ? 'Unpin from top' : 'Pin to top'} placement="right">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  togglePin(data.path);
                }}
                sx={{
                  position: 'absolute',
                  right: 4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  p: 0.4,
                  color: pinned ? 'primary.main' : 'text.disabled',
                  transition: 'color 0.15s',
                  zIndex: 1,
                  '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                }}
              >
                {pinned
                  ? <BookmarkOutlined sx={{ fontSize: 14 }} />
                  : <BookmarkBorderOutlined sx={{ fontSize: 14 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {!isCollapse && hasChildren && (
        <Collapse in={open} unmountOnExit>
          <List component="div" disablePadding>
            <NavSubList data={data.children} depth={depth} />
          </List>
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

NavSubList.propTypes = {
  data: PropTypes.array,
  depth: PropTypes.number,
};

function NavSubList({ data, depth }) {
  const { user, hasPermission } = useAuth();
  return (
    <>
      {data
        .filter((list) => isItemVisible(list, { user, hasPermission }))
        .map((list) => (
          <NavList key={list.title + list.path} data={list} depth={depth + 1} hasChildren={!!list.children} />
        ))}
    </>
  );
}
